import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Star } from 'react-feather';
import { servicesAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import AdsDisplay from '../components/AdsDisplay';
import { formatCurrencyWithConversion } from '../utils/currency';
import { getServiceColor, getServiceIcon, getServiceIconAsset, getServiceToken } from '../utils/serviceIcons';
import './Services.css';

const FAVORITES_KEY = 'nb_favorite_services';

export default function Services() {
  const { user } = useAuth();
  const [servicesMap, setServicesMap] = useState({});
  const [flatServices, setFlatServices] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]');
      setFavorites(Array.isArray(saved) ? saved : []);
    } catch {
      setFavorites([]);
    }
  }, []);

  useEffect(() => {
    servicesAPI.getAll()
      .then((response) => {
        setServicesMap(response.data?.services || {});
        setFlatServices(response.data?.flat || []);
      })
      .catch(() => setError('Failed to load services.'))
      .finally(() => setLoading(false));
  }, []);

  const favoriteSet = useMemo(() => new Set(favorites), [favorites]);

  const groupedCategories = useMemo(() => {
    const entries = Object.entries(servicesMap);
    const filteredEntries = search.trim()
      ? entries.filter(([category]) => category.toLowerCase().includes(search.trim().toLowerCase()))
      : entries;

    return filteredEntries.map(([category, services]) => ({
      category,
      services,
      iconToken: getServiceToken(category),
    }));
  }, [search, servicesMap]);

  const favoriteServices = useMemo(
    () => flatServices.filter((service) => favoriteSet.has(service.id)),
    [flatServices, favoriteSet]
  );

  const toggleFavorite = (serviceId) => {
    setFavorites((prev) => {
      const next = prev.includes(serviceId)
        ? prev.filter((id) => id !== serviceId)
        : [...prev, serviceId];
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(next));
      return next;
    });
  };

  return (
    <div className="services-page">
      <h1 className="page-title">Services</h1>
      <p className="page-subtitle">Browse our services by category and save your favorites.</p>

      <div className="card services-search-card">
        <div className="services-search-box">
          <Search size={18} className="services-search-icon" />
          <input
            className="form-input services-search-input"
            placeholder="Search category, e.g. TikTok followers"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {favoriteServices.length > 0 && (
        <div className="card services-favorites-card">
          <div className="services-section-title">Favorite Services</div>
          <div className="services-favorites-list">
            {favoriteServices.map((service) => (
              <button
                key={service.id}
                type="button"
                className="services-favorite-chip"
                onClick={() => toggleFavorite(service.id)}
              >
                <Star size={14} fill="currentColor" />
                <span>#{service.id}</span>
                <span>{service.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <AdsDisplay adSlot="banner-300x250" className="medium-rectangle" />

      <AdsDisplay adSlot="banner-320x50" className="banner" />

      {loading ? (
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <div className="spinner" style={{ margin: '0 auto' }} />
          <p style={{ marginTop: 12, color: 'var(--text-muted)' }}>Loading services...</p>
        </div>
      ) : error ? (
        <div className="alert alert-error">{error}</div>
      ) : groupedCategories.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>
          No categories match your search.
        </div>
      ) : (
        <div className="services-category-grid">
          {groupedCategories.map(({ category, services, iconToken }) => {
            const icon = getServiceIcon(iconToken);
            const iconColor = getServiceColor(iconToken);
            const iconAsset = getServiceIconAsset(iconToken);

            return (
              <div key={category} className="card services-category-card">
                <div className="services-category-header">
                  <div
                    className="services-category-badge"
                    style={{ background: `${iconColor}16`, color: iconColor, borderColor: `${iconColor}33` }}
                  >
                    {iconAsset ? (
                      <img src={iconAsset} alt="" className="services-category-image" />
                    ) : (
                      icon
                    )}
                  </div>
                  <div>
                    <div className="services-section-title">{category}</div>
                    <div className="services-count">{services.length} services</div>
                  </div>
                </div>

                <div className="services-list">
                  {services.map((service) => {
                    const isFavorite = favoriteSet.has(service.id);

                    return (
                      <div key={service.id} className="services-item">
                        <div className="services-item-top">
                          <button
                            type="button"
                            className={`services-star-btn ${isFavorite ? 'active' : ''}`}
                            onClick={() => toggleFavorite(service.id)}
                            aria-label={isFavorite ? 'Remove favorite' : 'Add favorite'}
                            title={isFavorite ? 'Remove favorite' : 'Add favorite'}
                          >
                            <Star size={15} fill={isFavorite ? 'currentColor' : 'none'} />
                          </button>
                          <div className="services-id">#{service.id}</div>
                        </div>

                        <div className="services-name">{service.name}</div>

                        <div className="services-meta">
                          <span>{formatCurrencyWithConversion(service.final_price || 0, user?.currency || 'USD', 4)}/1k</span>
                          <span>min {service.min?.toLocaleString()}</span>
                          <span>max {service.max?.toLocaleString()}</span>
                        </div>

                        <div className="services-item-actions">
                          <Link
                            to={`/dashboard/orders/new?category=${encodeURIComponent(category)}&service_id=${service.id}`}
                            className="services-open-order-link"
                          >
                            Open in New Order
                          </Link>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
